import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { PRODUCTS, GRADES, BRANDS } from '../../utils/DROPDOWN_OPTIONS';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import '../../styles/AnalyticsDashboard.css';


// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};

const RepackOutAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({});

  const products = PRODUCTS;
  const grades = GRADES;
  const brands = BRANDS;

  const [filters, setFilters] = useState({
    timePeriod: 'week',
    product: '',
    grade: '',
    brand: '',
    startDate: '',
    endDate: ''
  });

  const COLORS = ['#1565c0', '#10b981', '#f9a825', '#c62828', '#6a1b9a', '#0097a7', '#00838f', '#e65100', '#ad1457', '#283593'];

  useEffect(() => {
    fetchAnalyticsData();
  }, [filters]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await axios.get(`${API_BASE_URL}/repack_out_analytics.php?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data;
        
        // Convert all string numbers to actual numbers
        if (data.by_brand) {
          data.by_brand = data.by_brand.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0),
            percentage: parseFloat(item.percentage || 0)
          }));
        }
        
        if (data.by_pack_size) {
          data.by_pack_size = data.by_pack_size.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0),
            percentage: parseFloat(item.percentage || 0)
          }));
        }
        
        if (data.top_products) {
          data.top_products = data.top_products.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0),
            percentage: parseFloat(item.percentage || 0)
          }));
        }
        
        if (data.by_grade) {
          data.by_grade = data.by_grade.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0),
            percentage: parseFloat(item.percentage || 0)
          }));
        }
        
        if (data.by_shift) {
          data.by_shift = data.by_shift.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0)
          }));
        }
        
        if (data.daily_trend) {
          data.daily_trend = data.daily_trend.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0)
          }));
        }
        
        if (data.detailed_breakdown) {
          data.detailed_breakdown = data.detailed_breakdown.map(item => ({
            ...item,
            total_pounds: parseFloat(item.total_pounds || 0),
            total_cartons: parseInt(item.total_cartons || 0),
            percentage: parseFloat(item.percentage || 0)
          }));
        }
        
        setAnalyticsData(data);
        console.log('Analytics Data Set (converted to numbers):', data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      timePeriod: 'week',
      product: '',
      grade: '',
      brand: '',
      startDate: '',
      endDate: ''
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1e293b',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
        }}>
          <p style={{ 
            margin: '0 0 8px 0',
            fontWeight: 'bold',
            fontSize: '14px',
            color: '#60a5fa',
            borderBottom: '1px solid #374151',
            paddingBottom: '6px'
          }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ 
              margin: '4px 0',
              color: '#e5e7eb',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '12px',
                height: '12px',
                backgroundColor: entry.color,
                borderRadius: '2px',
                display: 'inline-block'
              }}></span>
              <strong style={{ color: '#f3f4f6' }}>{entry.name}:</strong>
              <span style={{ 
                fontWeight: 'bold',
                color: '#fbbf24',
                fontSize: '14px'
              }}>
                {formatNumber(entry.value)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const groupDataByProduct = (detailedBreakdown) => {
    if (!detailedBreakdown) return [];
    
    const grouped = {};
    
    detailedBreakdown.forEach(item => {
      if (!grouped[item.product]) {
        grouped[item.product] = {
          product: item.product,
          totalPounds: 0,
          totalCartons: 0,
          totalPercentage: 0,
          details: []
        };
      }
      
      grouped[item.product].totalPounds += parseFloat(item.total_pounds || 0);
      grouped[item.product].totalCartons += parseFloat(item.total_cartons || 0);
      grouped[item.product].totalPercentage += parseFloat(item.percentage || 0);
      grouped[item.product].details.push(item);
    });
    
    return Object.values(grouped).sort((a, b) => b.totalPounds - a.totalPounds);
  };

  const toggleProductExpand = (product) => {
    setExpandedProducts(prev => ({
      ...prev,
      [product]: !prev[product]
    }));
  };

  if (loading && !analyticsData) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>Loading Analytics...</p>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <h1>📊 Repack-Out Analytics Dashboard</h1>
        <p>Analyze repack-out trends and inventory metrics</p>
      </div>

      <div className="time-filter-card">
        <h2>🔍 Time Period Filter</h2>
        <div className="time-filter-controls">
          <div className="filter-group">
            <label>Time Period</label>
            <select 
              value={filters.timePeriod}
              onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
              className="filter-select"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {filters.timePeriod === 'custom' && (
            <>
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="filter-input"
                />
              </div>
            </>
          )}

          <button 
            className="reset-filters-btn" 
            onClick={handleReset}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
          >
            🔄 Reset All Filters
          </button>
        </div>
      </div>

      {analyticsData && (
        <div className="summary-cards">
          <div className="summary-card card-blue">
            <div className="card-icon">📦</div>
            <div className="card-content">
              <h3>Total Repack-Out</h3>
              <p className="card-value">{formatNumber(analyticsData.summary.total_pounds)} LBS</p>
              <p className="card-subtitle">{formatNumber(analyticsData.summary.total_pounds)} LBS</p>
            </div>
          </div>

          <div className="summary-card card-green">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h3>Total Cases</h3>
              <p className="card-value">{formatNumber(analyticsData.summary.total_cartons)}</p>
              <p className="card-subtitle">{analyticsData.summary.total_entries} Entries</p>
            </div>
          </div>

          <div className="summary-card card-orange">
            <div className="card-icon">🏷️</div>
            <div className="card-content">
              <h3>Unique Products</h3>
              <p className="card-value">{analyticsData.summary.unique_products}</p>
              <p className="card-subtitle">Products Tracked</p>
            </div>
          </div>

          <div className="summary-card card-purple">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <h3>Avg Per Entry</h3>
              <p className="card-value">{formatNumber(analyticsData.summary.avg_pounds_per_entry)} LBS</p>
              <p className="card-subtitle">Per Repack-Out Entry</p>
            </div>
          </div>
        </div>
      )}

      <div className="filter-chips-section">
        <div className="chips-category">
          <h3 className="chips-title">📦 Products</h3>
          <div className="chips-container">
            <button 
              className={`filter-chip ${filters.product === '' ? 'active' : ''}`}
              onClick={() => handleFilterChange('product', '')}
            >
              All
            </button>
            {products.map((product, index) => (
              <button
                key={index}
                className={`filter-chip ${filters.product === product ? 'active' : ''}`}
                onClick={() => handleFilterChange('product', product)}
              >
                {product}
              </button>
            ))}
          </div>
        </div>

        <div className="chips-category">
          <h3 className="chips-title">🏷️ Grades</h3>
          <div className="chips-container">
            <button 
              className={`filter-chip ${filters.grade === '' ? 'active' : ''}`}
              onClick={() => handleFilterChange('grade', '')}
            >
              All
            </button>
            {grades.map((grade, index) => (
              <button
                key={index}
                className={`filter-chip ${filters.grade === grade ? 'active' : ''}`}
                onClick={() => handleFilterChange('grade', grade)}
              >
                {grade}
              </button>
            ))}
          </div>
        </div>

        <div className="chips-category">
          <h3 className="chips-title">🎯 Brands</h3>
          <div className="chips-container">
            <button 
              className={`filter-chip ${filters.brand === '' ? 'active' : ''}`}
              onClick={() => handleFilterChange('brand', '')}
            >
              All
            </button>
            {brands.map((brand, index) => (
              <button
                key={index}
                className={`filter-chip ${filters.brand === brand ? 'active' : ''}`}
                onClick={() => handleFilterChange('brand', brand)}
              >
                {brand}
              </button>
            ))}
          </div>
        </div>
      </div>

      {analyticsData && (
        <>
          <div className="chart-card">
            <h2>🏆 Top Products by Repack-Out (LBS)</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={analyticsData.top_products}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="product" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="total_pounds" fill="#3b82f6" name="Total LBS" />
                <Bar dataKey="total_cartons" fill="#10b981" name="Cases" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h2>📊 Repack-Out by Grade</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={analyticsData.by_grade}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="grade" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="total_pounds" fill="#8b5cf6" name="Total LBS" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-row">
            <div className="chart-card chart-half">
              <h2>🎯 Repack-Out by Product</h2>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={analyticsData.top_products}
                    cx="50%"
                    cy="50%"
                    labelLine={{
                      stroke: '#94a3b8',
                      strokeWidth: 2
                    }}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      percent,
                      product,
                      total_cartons
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 40;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      
                      // Only show label if percentage is > 2%
                      if (percent < 0.02) return null;
                      
                      return (
                        <g style={{ pointerEvents: 'none', zIndex: 9999 }}>
                          <rect
                            x={x > cx ? x : x - 100}
                            y={y - 12}
                            width={x > cx ? 100 : 100}
                            height="24"
                            fill="white"
                            stroke="#cbd5e1"
                            strokeWidth="1"
                            rx="4"
                            opacity="1.0"
                            style={{ pointerEvents: 'none' }}
                          />
                          <text
                            x={x}
                            y={y}
                            fill="#0f172a"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{ 
                              fontSize: '13px', 
                              fontWeight: 'bold',
                              pointerEvents: 'none',
                              zIndex: 9999
                            }}
                          >
                            {`${product}: ${formatNumber(total_cartons)}`}
                          </text>
                        </g>
                      );
                    }}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="total_pounds"
                  >
                    {analyticsData.top_products.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '11px' }}
                    iconSize={10}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card chart-half">
              <h2>📦 Repack-Out by Grade</h2>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={analyticsData.by_grade}
                    cx="50%"
                    cy="50%"
                    labelLine={{
                      stroke: '#94a3b8',
                      strokeWidth: 2
                    }}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      percent,
                      grade,
                      total_cartons
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 40;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      
                      // Only show label if percentage is > 2%
                      if (percent < 0.02) return null;
                      
                      return (
                        <g style={{ pointerEvents: 'none', zIndex: 9999 }}>
                          <rect
                            x={x > cx ? x : x - 100}
                            y={y - 12}
                            width={x > cx ? 100 : 100}
                            height="24"
                            fill="white"
                            stroke="#cbd5e1"
                            strokeWidth="1"
                            rx="4"
                            opacity="1.0"
                            style={{ pointerEvents: 'none' }}
                          />
                          <text
                            x={x}
                            y={y}
                            fill="#0f172a"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{ 
                              fontSize: '13px', 
                              fontWeight: 'bold',
                              pointerEvents: 'none',
                              zIndex: 9999
                            }}
                          >
                            {`${grade}: ${formatNumber(total_cartons)}`}
                          </text>
                        </g>
                      );
                    }}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="total_pounds"
                  >
                    {analyticsData.by_grade.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '11px' }}
                    iconSize={10}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {analyticsData.daily_trend && analyticsData.daily_trend.length > 0 && (
            <div className="chart-card">
              <h2>📈 Daily Repack-Out Trend</h2>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analyticsData.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="total_pounds" stroke="#3b82f6" strokeWidth={2} name="Total LBS" />
                  <Line type="monotone" dataKey="total_cartons" stroke="#10b981" strokeWidth={2} name="Cases" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="chart-card">
            <h2>🌓 Repack-Out by Shift</h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={analyticsData.by_shift}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="shift" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="total_pounds" fill="#f59e0b" name="Total LBS" />
                <Bar dataKey="total_cartons" fill="#06b6d4" name="Cases" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="data-tables">
            <div className="table-card">
              <h2>📋 Top Products Detailed</h2>
              <div className="table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>Total LBS</th>
                      <th>Cases</th>
                      <th>Share %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.top_products.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td><strong>{item.product}</strong></td>
                        <td>{formatNumber(item.total_pounds)}</td>
                        <td>{formatNumber(item.total_cartons)}</td>
                        <td>
                          <span className="percentage-badge">{formatNumber(item.percentage)}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="table-card">
              <h2>🏷️ Grade Analysis</h2>
              <div className="table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Grade</th>
                      <th>Total LBS</th>
                      <th>Cases</th>
                      <th>Share %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.by_grade.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td><strong>{item.grade}</strong></td>
                        <td>{formatNumber(item.total_pounds)}</td>
                        <td>{formatNumber(item.total_cartons)}</td>
                        <td>
                          <span className="percentage-badge">{formatNumber(item.percentage)}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

            <div className="table-card" style={{ gridColumn: '1 / -1' }}>
              <h2>📋 Comprehensive Repack-Out Analysis (Grouped by Product)</h2>
              <div className="table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Product</th>
                      <th>Total LBS</th>
                      <th>Total Cases</th>
                      <th>Total Share %</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupDataByProduct(analyticsData.detailed_breakdown).map((group, groupIndex) => (
                      <React.Fragment key={groupIndex}>
                        <tr 
                          style={{ 
                            backgroundColor: 'white', 
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            color: '#1e293b'
                          }}
                          onClick={() => toggleProductExpand(group.product)}
                        >
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ fontSize: '16px', color: '#1e293b' }}>
                              {expandedProducts[group.product] ? '▼' : '▶'}
                            </span>
                          </td>
                          <td>
                            <strong style={{ fontSize: '15px', color: '#3b82f6' }}>
                              {group.product}
                            </strong>
                          </td>
                          <td><strong style={{ color: '#1e293b' }}>{formatNumber(group.totalPounds)}</strong></td>
                          <td><strong style={{ color: '#1e293b' }}>{formatNumber(group.totalCartons)}</strong></td>
                          <td>
                            <span className="percentage-badge" style={{ fontSize: '13px' }}>
                              {formatNumber(group.totalPercentage)}%
                            </span>
                          </td>
                          <td style={{ color: '#64748b' }}>{group.details.length} variants</td>
                        </tr>
                        
                        {expandedProducts[group.product] && (
                          <>
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                              <th></th>
                              <th style={{ fontSize: '12px', color: '#475569' }}>Grade</th>
                              <th style={{ fontSize: '12px', color: '#475569' }}>Pack Size</th>
                              <th style={{ fontSize: '12px', color: '#475569' }}>Brand</th>
                              <th style={{ fontSize: '12px', color: '#475569' }}>LBS</th>
                              <th style={{ fontSize: '12px', color: '#475569' }}>Cases</th>
                              <th style={{ fontSize: '12px', color: '#475569' }}>Share %</th>
                            </tr>
                            {group.details.map((detail, detailIndex) => (
                              <tr key={detailIndex} style={{ backgroundColor: 'white', color: '#1e293b' }}>
                                <td></td>
                                <td style={{ paddingLeft: '20px', color: '#1e293b' }}>{detail.grade}</td>
                                <td style={{ color: '#1e293b' }}>{detail.pack_size}</td>
                                <td style={{ color: '#1e293b' }}>{detail.brand}</td>
                                <td style={{ color: '#1e293b' }}>{formatNumber(detail.total_pounds)}</td>
                                <td style={{ color: '#1e293b' }}>{formatNumber(detail.total_cartons)}</td>
                                <td>
                                  <span className="percentage-badge" style={{ fontSize: '11px' }}>
                                    {formatNumber(detail.percentage)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
        </>
      )}

      {analyticsData && analyticsData.summary.total_entries === 0 && (
        <div className="no-data-message">
          <div className="no-data-icon">📊</div>
          <h3>No Data Available</h3>
          <p>Try adjusting your filters to see analytics data</p>
        </div>
      )}
    </div>
  );
};

export default RepackOutAnalytics;